import React, { useContext, useState } from 'react';
import { useLazyQuery } from '@apollo/client';
import { getScenario } from 'gqlQueries/priceSimulator';
import { useNavigate } from 'react-router-dom';
import _ from 'lodash';
import dayjs from 'dayjs';
import {
    portfolioUpdateKpiParser,
    simulationInputDataParser,
    simulationKpiParser,
    simulationOutputDataParser,
} from 'parser/priceSimulator/priceSimulatorDataParser';
import { UserContext } from 'contexts/ConfigContext';
import { getConfig } from 'utils/helperMethods';
import { ConfigKeys } from 'utils/Enums';
import { FilterContextData } from 'contexts/FilterContext';
const errorMessage: string = 'Something went wrong!';

type Props = {
    setSelectedDataSource: Function;
    setSelectedKpiValues: Function;
    setKpiMetrictsState: Function;
    setGlobalFilters: Function;
    setSimulationDateRange: Function;
    setSimulationOutputDataState: Function;
    setSimulationInputDataState: Function;
    setTitle: Function;
    setSimulationRecommendationDataState: Function;
    setSimulationTimePeriodSelection: Function;
    setIsEditFlowApiCalled: Function;
    setPortfolioUpdateKpisData: Function;
    setBackUpPortfolioUpdateKpisData: Function;
    setPriceEffectiveDate: Function;
    setCreatedByUser: Function;
    isViewMode: boolean;
};

function useFetchScenario({
    setSelectedDataSource,
    setSelectedKpiValues,
    setKpiMetrictsState,
    setGlobalFilters,
    setSimulationDateRange,
    setSimulationOutputDataState,
    setSimulationInputDataState,
    setTitle,
    setSimulationRecommendationDataState,
    setSimulationTimePeriodSelection,
    setIsEditFlowApiCalled,
    setPortfolioUpdateKpisData,
    setBackUpPortfolioUpdateKpisData,
    setPriceEffectiveDate,
    setCreatedByUser,
    isViewMode,
}: Props) {
    const [loading, setLoading] = useState<boolean>(false);
    const [error, setError] = useState<null | string>(null);
    const [data, setData] = useState<Array<string> | null>([]);
    const navigate = useNavigate();
    const { appConfig } = useContext(UserContext);
    const { applyGlobalFilterHandler } = useContext(FilterContextData);
    const { inputKeyMapping, levels, outputKeyMapping, recommendationKeyMapping, dataSources } = getConfig(
        ConfigKeys.PRICE_SIMULATOR,
        appConfig,
    );
    const [fetchScenario] = useLazyQuery(getScenario, {
        onCompleted: onFetchComplete,
        onError: onFetchError,
        fetchPolicy: 'no-cache',
    });

    //called when fetching is complete
    function onFetchComplete(payload: any) {
        try {
            const { getScenarioCompleteData } = payload;

            if (getScenarioCompleteData && 'userName' in getScenarioCompleteData) {
                setCreatedByUser(getScenarioCompleteData?.userName);
            }

            // update dataSource
            if (getScenarioCompleteData && 'dataSourceType' in getScenarioCompleteData) {
                setSelectedDataSource(
                    dataSources.find((i: any) => i.value === getScenarioCompleteData?.dataSourceType),
                );
            }

            // apply scenario filters to Global filters
            if (getScenarioCompleteData && 'presetFilterJson' in getScenarioCompleteData && !isViewMode) {
                applyGlobalFilterHandler(getScenarioCompleteData.presetFilterJson);
            }

            //parse goal inputs
            if (
                getScenarioCompleteData &&
                'goalInputs' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.goalInputs)
            ) {
                const parsedKpiMetricData = getScenarioCompleteData.goalInputs?.map((item: any) => JSON.parse(item));
                setKpiMetrictsState(parsedKpiMetricData);
                const selectedKeys = parsedKpiMetricData
                    .filter((item: any) => item.selected)
                    .map((kpi: any) => kpi.key);

                setSelectedKpiValues(selectedKeys);
            }
            //parse global filter
            if (
                getScenarioCompleteData &&
                'globalFilter' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.globalFilter)
            ) {
                setGlobalFilters([...getScenarioCompleteData.globalFilter?.map((item: any) => JSON.parse(item))]);
            }
            //parse start and end date
            if (
                getScenarioCompleteData &&
                'startDate' in getScenarioCompleteData &&
                'endDate' in getScenarioCompleteData
            ) {
                const startDate = dayjs(getScenarioCompleteData.startDate).add(6, 'day').toDate();
                const endDate = dayjs(getScenarioCompleteData.endDate).add(6, 'day').toDate();
                setSimulationDateRange([startDate, endDate]);
            }
            //parse price effective date
            if (getScenarioCompleteData && getScenarioCompleteData?.priceEffectiveDate) {
                const endDate = dayjs(getScenarioCompleteData.priceEffectiveDate).add(6, 'day').toDate();
                setPriceEffectiveDate([null, endDate]);
            }
            //parse simulator output
            if (
                getScenarioCompleteData &&
                'simulatorOutput' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.simulatorOutput)
            ) {
                const parsedData: any = simulationOutputDataParser(
                    getScenarioCompleteData?.simulatorOutput,
                    outputKeyMapping,
                );
                setSimulationOutputDataState(parsedData);
            }
            //parse portfolio update kpi data
            if (
                getScenarioCompleteData &&
                'portfolioUpdateData' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.portfolioUpdateData)
            ) {
                const parsedPortfolioUpdateKpiData = getScenarioCompleteData.portfolioUpdateData?.map((item: any) =>
                    JSON.parse(item),
                );
                const parsedData: any = portfolioUpdateKpiParser(parsedPortfolioUpdateKpiData);
                setPortfolioUpdateKpisData(_.cloneDeep(parsedData));
                setBackUpPortfolioUpdateKpisData(_.cloneDeep(parsedData));
            }
            //parse simulator kpis, since simulator output will be coming in output call and would have missed in update scenario call
            if (
                getScenarioCompleteData &&
                'simulatorKpis' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.simulatorKpis)
            ) {
                setKpiMetrictsState((oldData: any) => {
                    const parsedData: any = simulationKpiParser(getScenarioCompleteData?.simulatorKpis, oldData, true);
                    return [...parsedData];
                });
            }
            //parse simulator inputs
            if (
                getScenarioCompleteData &&
                'simulatorInputs' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.simulatorInputs)
            ) {
                const parsedData: any = simulationInputDataParser(
                    getScenarioCompleteData.simulatorInputs,
                    levels,
                    inputKeyMapping,
                );
                setSimulationInputDataState([...parsedData]);
            }
            if (
                getScenarioCompleteData &&
                'recommendationOutput' in getScenarioCompleteData &&
                Array.isArray(getScenarioCompleteData.recommendationOutput)
            ) {
                const parsedData: any = simulationOutputDataParser(
                    getScenarioCompleteData.recommendationOutput,
                    recommendationKeyMapping,
                );
                setSimulationRecommendationDataState([...parsedData]);
            }

            //parse scenario name
            if (getScenarioCompleteData && 'scenarioName' in getScenarioCompleteData) {
                setTitle(getScenarioCompleteData?.scenarioName);
            }

            // parse start and end date
            if (getScenarioCompleteData && 'startDate' in getScenarioCompleteData) {
                const utcOffset = dayjs().utcOffset();
                const startDateUnix = dayjs(getScenarioCompleteData?.startDate)
                    .add(utcOffset, 'm')
                    .subtract(6, 'day')
                    .unix();
                const endDateUnix = dayjs(getScenarioCompleteData?.endDate)
                    .add(utcOffset, 'm')
                    .subtract(6, 'day')
                    .unix();
                setSimulationTimePeriodSelection(
                    JSON.stringify({
                        start_timestamp: startDateUnix,
                        end_timestamp: endDateUnix,
                    }),
                );
            }
            // adding to stop useEffect triggering current price change api call
            setTimeout(() => {
                setIsEditFlowApiCalled(true);
            }, 2000);
        } catch (error) {
            console.error(errorMessage, error);
            onFetchError(error);
        } finally {
            setLoading(false);
        }
    }

    //called when API throws an error
    function onFetchError(error: any = errorMessage) {
        navigate('/pricing/recommendation/price-simulator', { replace: false });
        setData([]);
        setLoading(false);
        setError(error);
    }

    return {
        loading,
        error,
        data,
        fetchScenario: (variables: any) => {
            setLoading(true);
            setError(null);
            fetchScenario(variables);
        },
    };
}

export { useFetchScenario };
